import { Stack } from '@mui/material';
import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';

import { Payment } from '@woovi/components';
import { CHARGE_STATUS_ENUM } from '@woovi/shared';

import type { Order_charge$key } from './__generated__/Order_charge.graphql.ts';
import type { Order_paymentLink$key } from './__generated__/Order_paymentLink.graphql.ts';
import type { Order_query$key } from './__generated__/Order_query.graphql.ts';
import useStoreContext from '../store/useStoreContext.tsx';

const useOnCloseSuccess = (
  charge: Order_charge$key,
  onCloseSuccess: () => void,
) => {
  const { openPixState, setOpenPixState } = useStoreContext();

  useEffect(() => {
    if (charge?.status) {
      setOpenPixState({ status: charge?.status });

      const { closeOnSuccess } = openPixState;

      // to avoid breaking changes from websocket
      if (charge.status === CHARGE_STATUS_ENUM.COMPLETED && closeOnSuccess) {
        onCloseSuccess();
      }
    }
  }, [charge?.status]);
};

export type OrderProps = {
  query: Order_query$key;
  charge: Order_charge$key;
  paymentLink: Order_paymentLink$key;
  onCloseSuccess: () => void;
};

const Order = ({ onCloseSuccess, ...props }: OrderProps): ReactNode => {
  const { openPixState, setOpenPixState } = useStoreContext();

  const query = useFragment<Order_query$key>(
    graphql`
      fragment Order_query on Query {
        # eslint-disable-next-line relay/must-colocate-fragment-spreads
        ...Payment_query
      }
    `,
    props.query,
  );

  const charge = useFragment<Order_charge$key>(
    graphql`
      fragment Order_charge on Charge {
        id
        status
        # eslint-disable-next-line relay/must-colocate-fragment-spreads
        ...Payment_charge
      }
    `,
    props.charge,
  );

  const paymentLink = useFragment<Order_paymentLink$key>(
    graphql`
      fragment Order_paymentLink on PaymentLink {
        ...Payment_paymentLink
      }
    `,
    props.paymentLink,
  );

  useOnCloseSuccess(charge, onCloseSuccess);

  return (
    <Stack
      sx={{
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        padding: 2,
      }}
    >
      <Payment
        allowSimulatePayment={openPixState.allowSimulatePayment}
        alwaysShowQrCode={openPixState.alwaysShowQrCode}
        query={query}
        charge={charge}
        paymentLink={paymentLink}
        pixQrCode={null}
        shouldRedirect
      />
    </Stack>
  );
};

export default Order;
